// 메신저 «보는 중» 판정 + 메신저 iframe(MM) 자체 알림 음소거(patch_mm_mute_alert_while_viewing.md).
//
// 알림이 두 번 오던 원인: 같은 메시지를 MM 웹앱이 iframe 안에서 브라우저 Notification 으로 한 번(즉시),
// 게이트웨이 서버가 Web Push 로 또 한 번(5~11초 뒤) 띄웠다. 그래서
//  ① 이 브라우저에 게이트웨이 푸시 구독이 있으면 MM 자체 알림을 띄우지 않는다(muteMmNotifications).
//     구독이 없는 브라우저에서는 MM 알림이 유일한 알림이라 그대로 둔다.
//     구독을 켜고 끄면(MyPage·utils/push·서비스워커 회전) 새로고침 없이 바로 다시 읽는다 — 다른 탭에도 알린다
//     (notePushSubscriptionChanged · 켠 직후 낡은 pushOn 으로 알림이 2번·0번 뜨던 문제).
//  ② 메신저를 보고 있으면(메인·옆 창 + 탭이 보임 + 포커스) 서버에 20초마다 알리고,
//     서버(mm_notify._push)가 45초 안에 신호가 있으면 그 사용자의 메신저 푸시를 건너뛴다(모든 기기).
//     🔴 서비스워커에서 표시만 거르면 안 된다 — 알림 없이 끝난 push 가 쌓이면 브라우저가 구독을 끊을 수 있다.
import { pushAPI } from './api/client';

export const VIEWING_HEARTBEAT_MS = 20000;
const MUTE_MARK = '__aigwMmNotifyMuted';

let shown = false;     // SplitShell 이 갱신: 메인 또는 옆 창에 메신저가 떠 있는가
let pushOn = false;    // 이 브라우저에 게이트웨이 푸시 구독이 있는가(비동기로 갱신, 기본 false)
const shownListeners = new Set();   // startViewingHeartbeat 가 건 재판정 함수

export function setMessengerShown(v) {
  shown = !!v;
  shownListeners.forEach((fn) => {
    try { fn(); } catch (e) { /* 재판정 실패가 화면을 깨지 않게 */ }
  });
}

export function isMessengerOnScreen() {
  try {
    // 메신저 iframe 안에 포커스가 있어도 최상위 문서의 hasFocus 는 true 다.
    return !!(shown
      && typeof document !== 'undefined'
      && document.visibilityState === 'visible'
      && document.hasFocus());
  } catch (e) {
    return false;
  }
}

// navigator.serviceWorker.getRegistration() → pushManager.getSubscription() 이 있으면 pushOn=true.
// 어느 단계든 없거나 실패하면 false.
// EmbedPage 폴링(50ms)이 연달아 부르므로 진행 중인 조회가 있으면 그 결과를 같이 쓴다.
let pushOnInFlight = null;
// 구독이 바뀔 때마다 1씩 올린다 — 바뀌기 «전에» 시작한 조회가 바뀐 «뒤에» 끝나면 옛 값을 쓰지 않는다.
let pushOnGen = 0;

export function refreshPushOn() {
  if (pushOnInFlight) return pushOnInFlight;
  try {
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : null;
    if (!sw || typeof sw.getRegistration !== 'function') {
      pushOn = false;
      return Promise.resolve(false);
    }
    const gen = pushOnGen;
    const p = Promise.resolve(sw.getRegistration())
      .then((reg) => {
        const pm = reg && reg.pushManager;
        if (!pm || typeof pm.getSubscription !== 'function') return null;
        return pm.getSubscription();
      })
      .then((sub) => {
        if (gen === pushOnGen) pushOn = !!sub;
        return pushOn;
      })
      .catch(() => {
        if (gen === pushOnGen) pushOn = false;
        return pushOn;
      })
      .then((v) => {
        // 뒤에 시작한 조회의 진행 표시는 지우지 않는다.
        if (pushOnInFlight === p) pushOnInFlight = null;
        return v;
      });
    pushOnInFlight = p;
    return p;
  } catch (e) {
    pushOnInFlight = null;
    pushOn = false;
    return Promise.resolve(false);
  }
}

// 다른 탭에 «구독이 바뀌었다» 를 알리는 채널(같은 origin 의 탭끼리 구독을 공유하는데 pushOn 은 탭마다 따로다).
// 🔴 보내는 채널과 듣는 채널은 같은 객체 하나 — 자기가 보낸 메시지는 자기 객체로 돌아오지 않는다.
const PUSH_SUB_CHANNEL = 'aigw-push-sub';
let pushSubChannel = null;

// 같은 탭 안에서 «구독이 바뀌었다» 를 듣는 쪽(deviceActivity — 구독 endpoint 캐시를 비운다 · n2b #1277).
// 이 탭이 바꿈(notePushSubscriptionChanged)·다른 탭 채널·서비스워커 회전(rereadPushOn) 세 경로 모두에서 부른다.
// 🔴 듣는 쪽은 채널에 아무것도 보내지 않는다(메아리 금지 규칙 그대로).
const pushSubListeners = new Set();

// 반환 = 해제 함수(함수가 아닌 인자면 아무것도 안 하는 해제 함수).
export function onPushSubscriptionChanged(listener) {
  if (typeof listener !== 'function') return () => {};
  pushSubListeners.add(listener);
  return () => { pushSubListeners.delete(listener); };
}

function firePushSubChanged() {
  Array.from(pushSubListeners).forEach((fn) => {
    try { fn(); } catch (e) { /* 듣는 쪽 실패가 다른 듣는 쪽·부른 쪽을 깨지 않게 */ }
  });
}

function rereadPushOn() {
  pushOnGen += 1;
  firePushSubChanged();
  pushOnInFlight = null;
  return refreshPushOn();
}

// 이 브라우저의 푸시 구독을 바꾼 쪽(MyPage·utils/push)이 성공·실패와 무관하게 부른다.
// 값을 받지 않는다 — 실제 브라우저 구독을 다시 읽는다. 반환 = 그 조회의 Promise<boolean>. 절대 throw 하지 않는다.
export function notePushSubscriptionChanged() {
  try {
    pushOnGen += 1;
    firePushSubChanged();
    pushOnInFlight = null;
    try {
      if (pushSubChannel) pushSubChannel.postMessage({ type: 'changed' });
    } catch (e) { /* 다른 탭은 다음 focus 때 다시 읽는다 */ }
    return refreshPushOn();
  } catch (e) {
    return Promise.resolve(pushOn);
  }
}

// 다른 탭(채널)·서비스워커(pushsubscriptionchange)가 알려 오면 다시 읽기만 한다.
// 🔴 받은 쪽은 다시 보내지 않는다 — 되쏘면 탭끼리 끝없이 주고받는다.
try {
  if (typeof BroadcastChannel === 'function') {
    pushSubChannel = new BroadcastChannel(PUSH_SUB_CHANNEL);
    pushSubChannel.onmessage = (ev) => {
      const d = ev && ev.data;
      if (d && d.type === 'changed') rereadPushOn();
    };
  }
} catch (e) { pushSubChannel = null; }

try {
  const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : null;
  if (sw && typeof sw.addEventListener === 'function') {
    // onmessage 가 아니라 addEventListener — 다른 코드가 onmessage 를 쓸 때 덮지 않는다.
    sw.addEventListener('message', (ev) => {
      const d = ev && ev.data;
      if (d && d.type === 'aigw-push-sub-changed') rereadPushOn();
    });
    // addEventListener 만으로는 메시지 큐가 늦게 열릴 수 있다.
    if (typeof sw.startMessages === 'function') sw.startMessages();
  }
} catch (e) { /* 수신을 못 걸어도 focus·iframe 재로드 때 다시 읽는다(지금과 같다) */ }

function sendViewing(viewing) {
  try {
    if (!pushAPI || typeof pushAPI.messengerViewing !== 'function') return;
    const p = pushAPI.messengerViewing(viewing);
    if (p && typeof p.catch === 'function') p.catch(() => { /* 실패는 삼킨다(다음 주기에 다시 보낸다) */ });
  } catch (e) { /* 실패는 삼킨다 */ }
}

// SplitShell 이 마운트 때 건다. 반환값 = 해제 함수.
export function startViewingHeartbeat() {
  let viewing = false;
  let timer = null;
  let blurTimer = null;
  let stopped = false;

  const stopTimer = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const evaluate = () => {
    if (stopped) return;
    const now = isMessengerOnScreen();
    if (now && !viewing) {
      viewing = true;
      sendViewing(true);
      stopTimer();
      timer = setInterval(() => {
        if (isMessengerOnScreen()) sendViewing(true);
        else evaluate();   // 이벤트 없이 바뀐 경우 — 여기서 false 를 한 번 보낸다
      }, VIEWING_HEARTBEAT_MS);
    } else if (!now && viewing) {
      // 보고 있음 → 아님: 바로 알려 45초를 기다리지 않게 한다.
      viewing = false;
      stopTimer();
      sendViewing(false);
    }
  };

  const onVisibility = () => evaluate();
  const onFocus = () => {
    refreshPushOn();
    evaluate();
  };
  // 🔴 blur 는 포커스가 메신저 iframe 안으로 들어갈 때도 뜬다(최상위 hasFocus 는 그대로 true) →
  //    바로 «아님»으로 보지 말고 다음 틱에 다시 판정한다.
  const onBlur = () => {
    if (blurTimer) clearTimeout(blurTimer);
    blurTimer = setTimeout(() => {
      blurTimer = null;
      evaluate();
    }, 0);
  };

  shownListeners.add(evaluate);
  try {
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
  } catch (e) { /* 리스너를 못 걸어도 주기 판정은 setMessengerShown 으로 돈다 */ }
  evaluate();

  return () => {
    stopped = true;
    stopTimer();
    if (blurTimer) {
      clearTimeout(blurTimer);
      blurTimer = null;
    }
    shownListeners.delete(evaluate);
    try {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    } catch (e) { /* */ }
  };
}

function silentNotification() {
  return {
    close() {},
    addEventListener() {},
    removeEventListener() {},
    onclick: null,
    onclose: null,
    onerror: null,
    onshow: null,
  };
}

// MM 이 띄운 진짜 알림의 클릭 처리기(n.onclick = h — iframe 안에서 그 글로 이동)를 감싸 h 를 부른 «뒤»
// onClick(게이트웨이를 메신저 화면으로)을 부른다(patch_mm_push_click_opens_post.md §3-4).
// onclick 은 Notification.prototype 의 접근자(이벤트 처리기 속성)라, 그 인스턴스에 같은 이름의 접근자를 얹어
// 대입만 가로채고 실제 등록은 원래 setter 로 한다. 감싸기 실패 = MM 처리기만 돈다(지금과 같다).
function chainNotificationClick(n, onClick) {
  if (!n || typeof onClick !== 'function') return;
  try {
    let desc = null;
    for (let p = Object.getPrototypeOf(n); p && !desc; p = Object.getPrototypeOf(p)) {
      desc = Object.getOwnPropertyDescriptor(p, 'onclick') || null;
    }
    if (!desc || typeof desc.set !== 'function') return;
    let handler = null;
    Object.defineProperty(n, 'onclick', {
      configurable: true,
      enumerable: true,
      get() { return handler; },
      set(h) {
        handler = typeof h === 'function' ? h : null;
        const run = handler;
        desc.set.call(n, run ? function onMmNotificationClick(ev) {
          try {
            return run.call(this, ev);
          } finally {
            try { onClick(ev); } catch (e) { /* 게이트웨이 이동 실패가 MM 처리를 깨지 않게 */ }
          }
        } : null);
      },
    });
  } catch (e) { /* */ }
}

// MM iframe 문서의 Notification 을 Proxy(construct 트랩)로 감싼다.
// (pushOn || isMessengerOnScreen()) 이면 원래 생성자를 부르지 않고 가짜 객체를 돌려준다.
// 끄지 않을 때는 진짜 알림을 만들고, onClick 이 있으면 그 클릭 처리기 뒤에 onClick 을 덧붙인다
// (게이트웨이가 다른 화면이면 메신저 iframe 이 숨어 있어 MM 처리기만으로는 화면이 안 바뀐다).
// permission·requestPermission 등 정적 속성은 트랩이 없어 그대로 보인다.
// 표식은 그 iframe 창(doc.defaultView)에 단다 — iframe 이 다시 로드되면 새 창이라 다시 감싼다
// (EmbedPage.jsx onLoad 가 재로드마다 다시 부른다).
export function muteMmNotifications(doc, onClick) {
  refreshPushOn();
  try {
    const win = doc && doc.defaultView;
    if (!win || win[MUTE_MARK]) return;
    const Original = win.Notification;
    if (typeof Original !== 'function') return;
    const Muted = new Proxy(Original, {
      construct(target, args, newTarget) {
        if (pushOn || isMessengerOnScreen()) return silentNotification();
        const n = Reflect.construct(target, args, newTarget === Muted ? target : newTarget);
        chainNotificationClick(n, onClick);
        return n;
      },
    });
    win.Notification = Muted;
    if (win.Notification === Muted) win[MUTE_MARK] = true;
  } catch (e) { /* 감싸기 실패 = MM 알림이 지금처럼 뜬다(화면은 그대로) */ }
}
